import { useState } from "react";
import type { CharacterState, Pact, Rules } from "@/data/types";
import { calcDerivedStats } from "@/lib/characterCalc";
import { emptyPact } from "./shared";
import { OfferingGrid } from "./OfferingGrid";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Plus, Trash2, Dice5, Pencil } from "lucide-react";

type PactPanel = "offering" | "patron" | null;

export function StepDarkOfferings({
  state,
  rules,
  stats,
  onChange,
  maxPacts,
}: {
  state: CharacterState;
  rules: Rules;
  stats: ReturnType<typeof calcDerivedStats>;
  onChange: (s: CharacterState) => void;
  maxPacts: number;
}) {
  // Per-pact open panel: 'offering' grid, 'patron' grid, or null (complete/collapsed)
  // Undefined key = derive from pact state (auto-open when incomplete)
  const [openPanels, setOpenPanels] = useState<Record<number, PactPanel>>({});

  const getPanelMode = (pact: Pact, idx: number): PactPanel => {
    if (idx in openPanels) return openPanels[idx];
    if (!pact.darkOfferingId) return "offering";
    if (!pact.patronId) return "patron";
    return null;
  };

  const setPanel = (idx: number, panel: PactPanel) =>
    setOpenPanels((prev) => ({ ...prev, [idx]: panel }));

  const canAdd =
    state.pacts.length < maxPacts &&
    state.pacts.length < stats.darkOfferingsLimit;

  const addPact = () => {
    onChange({ ...state, pacts: [...state.pacts, emptyPact()] });
    // New pact has no offering, will auto-open offering grid
  };

  const removePact = (idx: number) => {
    const newPacts = state.pacts.filter((_, i) => i !== idx);
    setOpenPanels({}); // reset all: indices shift after removal
    onChange({ ...state, pacts: newPacts });
  };

  const selectOffering = (pactIdx: number, offeringId: number) => {
    const newPacts = state.pacts.map((p, i) =>
      i === pactIdx ? { ...p, darkOfferingId: offeringId, patronId: "" } : p
    );
    setPanel(pactIdx, "patron");
    onChange({ ...state, pacts: newPacts });
  };

  const selectPatron = (pactIdx: number, patronId: string) => {
    const newPacts = state.pacts.map((p, i) =>
      i === pactIdx ? { ...p, patronId } : p
    );
    setPanel(pactIdx, null); // collapse — pact is complete
    onChange({ ...state, pacts: newPacts });
  };

  const randomizeOffering = (pactIdx: number) => {
    const takenByOthers = state.pacts
      .filter((_, i) => i !== pactIdx)
      .map((p) => p.darkOfferingId)
      .filter(Boolean);
    const available = rules.darkOfferings.filter(
      (o) => !takenByOthers.includes(o.id)
    );
    const pick = available[Math.floor(Math.random() * available.length)];
    if (pick) selectOffering(pactIdx, pick.id);
  };

  const randomizePatron = (pactIdx: number) => {
    // Previous Patrons must be chosen deliberately, never at random.
    const candidates = rules.patrons.filter((p) => !p.exiled);
    const pick = candidates[Math.floor(Math.random() * candidates.length)];
    if (pick) selectPatron(pactIdx, pick.id);
  };

  const usedOfferingIds = state.pacts
    .map((p) => p.darkOfferingId)
    .filter(Boolean);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold mb-1">Dark Offerings &amp; Patrons</h2>
        <p className="text-muted-foreground text-sm">
          Make pacts with otherworldly patrons. Each pact requires a Dark
          Offering — a piece of your soul given away permanently. You may make
          up to {maxPacts} pacts (max {stats.darkOfferingsLimit} total Dark
          Offerings).
        </p>
      </div>

      {stats.totalDarkOfferings >= stats.darkOfferingsLimit - 1 && (
        <div className="flex items-center gap-2 text-sm text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-950/30 rounded-md p-3">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          {stats.totalDarkOfferings >= stats.darkOfferingsLimit
            ? "You have reached the maximum number of Dark Offerings — your character will die if you exceed this!"
            : "Warning: you are approaching the Dark Offerings limit."}
        </div>
      )}

      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          {state.pacts.length} / {maxPacts} pacts
        </span>
      </div>

      <div className="space-y-4">
        {state.pacts.length === 0 && (
          <div className="text-center py-8 text-muted-foreground text-sm border-2 border-dashed rounded-lg">
            No pacts yet. Click &quot;Add Pact&quot; to begin your dealings with
            the beyond.
          </div>
        )}

        {state.pacts.map((pact, idx) => {
          const panelMode = getPanelMode(pact, idx);
          const offering = rules.darkOfferings.find(
            (o) => o.id === pact.darkOfferingId
          );
          const patron = rules.patrons.find((p) => p.id === pact.patronId);

          return (
            <Card key={idx}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <CardTitle className="text-sm">Pact {idx + 1}</CardTitle>
                    {offering && (
                      <Badge variant="destructive" className="text-xs">
                        {offering.name}
                      </Badge>
                    )}
                    {patron && (
                      <Badge variant="outline" className="text-xs">
                        {patron.name}
                      </Badge>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 shrink-0"
                    onClick={() => removePact(idx)}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              </CardHeader>

              <CardContent className="space-y-3 pt-0">
                {/* ── OFFERING SELECTION GRID ── */}
                {panelMode === "offering" && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium text-muted-foreground">
                        Choose a Dark Offering
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 gap-1.5 text-xs"
                        onClick={() => randomizeOffering(idx)}
                      >
                        <Dice5 className="h-3.5 w-3.5" />
                        Random
                      </Button>
                    </div>
                    <OfferingGrid
                      offerings={rules.darkOfferings}
                      onSelect={(id) => selectOffering(idx, id)}
                      disabledIds={
                        new Set(
                          (usedOfferingIds as number[]).filter(
                            (id) => id !== pact.darkOfferingId
                          )
                        )
                      }
                      disabledLabel="Taken by another pact"
                      selectedId={pact.darkOfferingId}
                      showWealth={true}
                    />
                  </div>
                )}

                {/* ── PATRON SELECTION GRID ── */}
                {panelMode === "patron" && (
                  <div>
                    {/* Selected offering summary */}
                    {offering && (
                      <div className="flex items-start justify-between gap-2 mb-3 p-2.5 rounded-md bg-destructive/10 border border-destructive/20">
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-destructive">
                            {offering.name}
                          </p>
                          <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                            {offering.description}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs shrink-0"
                          onClick={() => setPanel(idx, "offering")}
                        >
                          <Pencil className="h-3 w-3 mr-1" />
                          Change
                        </Button>
                      </div>
                    )}

                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-muted-foreground">
                        Choose a Patron
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => randomizePatron(idx)}
                      >
                        <Dice5 className="h-3 w-3 mr-1" />
                        Random
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {rules.patrons.map((p) => {
                        return (
                          <button
                            key={p.id}
                            onClick={() => selectPatron(idx, p.id)}
                            className="text-left rounded-md border p-3 transition-colors w-full hover:border-primary hover:bg-accent cursor-pointer"
                          >
                            <p className="font-semibold text-sm flex items-center gap-2">
                              {p.name}
                              {p.exiled && (
                                <Badge
                                  variant="outline"
                                  className="text-[10px]"
                                >
                                  Previous Patron
                                </Badge>
                              )}
                            </p>
                            <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug line-clamp-2">
                              {p.description}
                            </p>
                            <ul className="mt-1.5 space-y-0.5">
                              {p.challenges.map((c) => (
                                <li
                                  key={c}
                                  className="text-[11px] text-muted-foreground flex items-start gap-1"
                                >
                                  <span className="shrink-0">•</span>
                                  <span>{c}</span>
                                </li>
                              ))}
                            </ul>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── COMPLETE / COLLAPSED VIEW ── */}
                {panelMode === null && (
                  <div className="space-y-2">
                    {/* Offering row */}
                    <div className="flex items-start justify-between gap-2 rounded-md bg-destructive/10 border border-destructive/20 p-2.5">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-destructive">
                          {offering?.name}
                        </p>
                        <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                          {offering?.description}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs shrink-0"
                        onClick={() => setPanel(idx, "offering")}
                      >
                        <Pencil className="h-3 w-3 mr-1" />
                        Change
                      </Button>
                    </div>

                    {/* Patron row */}
                    <div className="flex items-start justify-between gap-2 rounded-md border p-2.5">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold">{patron?.name}</p>
                        <ul className="mt-0.5 space-y-0.5">
                          {patron?.challenges.map((c) => (
                            <li
                              key={c}
                              className="text-[11px] text-muted-foreground flex items-start gap-1"
                            >
                              <span className="shrink-0">•</span>
                              <span>{c}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs shrink-0"
                        onClick={() => setPanel(idx, "patron")}
                      >
                        <Pencil className="h-3 w-3 mr-1" />
                        Change
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Button onClick={addPact} disabled={!canAdd} size="sm">
        <Plus className="h-4 w-4 mr-1" />
        Add Pact
      </Button>
    </div>
  );
}
